// users_manager.jsx
// The registered-users table and the account drawer behind it: balance, plan,
// codes, scan analytics, the credit ledger and the plan history — plus the two
// things support actually needs to DO, which are moving credits and changing a
// subscription.
//
// Two behaviours worth knowing before reading the code:
//
//  • Credit removals CLAMP at the floor server-side. Taking 10 off an account
//    holding 4 applies 4, and the response says so. The UI reports what was
//    applied rather than echoing what was typed, because those differ.
//  • "Pro" in this table is the DERIVED entitlement, not the plan column. An
//    expired pro row is a free user, and PlanBadge shows the stored plan beside
//    the badge when the two disagree.

import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Users, Search, RefreshCw, Coins, QrCode, ScanLine, Mail, Phone, Calendar,
  ArrowUpDown, CreditCard, History, ShieldCheck, Plus, Minus, X,
} from 'lucide-react';
import { useQRCreate } from '../../authentication_components/qrCreateContext';
import {
  Section, Modal, Field, Button, Loading, EmptyState, Pagination, Meter, Stat, ScanRollup,
  StatusBadge, PlanBadge, Badge, inputClass, fmtInt, fmtDate, fmtDateTime, fullName,
} from './qrcreate_ui';

const PAGE_SIZE = 25;
const PLANS = ['free', 'pro'];
const STATUSES = ['active', 'trialing', 'grace', 'canceled', 'expired'];
const PROVIDERS = ['manual', 'apple', 'google', 'stripe'];

const SORTS = [
  { id: 'created', label: 'Newest signups' },
  { id: 'last_login', label: 'Recently active' },
  { id: 'scans', label: 'Most scans' },
  { id: 'codes', label: 'Most codes' },
  { id: 'credits', label: 'Most credits' },
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
];

const AUTH_LABELS = { email: 'Email OTP', google: 'Google', apple: 'Apple' };

// The API takes a date; an <input type="date"> gives one back. Anything already
// a full timestamp is trimmed to its date part for display in the form.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

export default function UsersManager({ focusUserId, onFocusHandled }) {
  const { fetchUsers, getUser } = useQRCreate();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState('');
  const [entitled, setEntitled] = useState('');
  const [sort, setSort] = useState('created');
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); setSearch(searchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const result = await fetchUsers({
        search: search || undefined,
        plan: plan || undefined,
        entitled: entitled || undefined,
        sort,
        order: sort === 'name' || sort === 'email' ? 'asc' : 'desc',
        limit: PAGE_SIZE,
        offset,
      });
      setData({ users: result?.users || [], total: result?.total || 0 });
    } catch (err) {
      toast.error(err.message || 'Failed to load registered users');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchUsers, search, plan, entitled, sort, offset]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (userId) => {
    setDetailLoading(true);
    try {
      const result = await getUser(userId, { days: 30, limit: 50 });
      setDetail(result || null);
    } catch (err) {
      toast.error(err.message || 'Failed to load the user');
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [getUser]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  // Another tab asked for one account to be opened (a code or scan row).
  useEffect(() => {
    if (focusUserId) {
      setSelectedId(focusUserId);
      onFocusHandled?.();
    }
  }, [focusUserId, onFocusHandled]);

  // A write changed this account: refresh the drawer and the row behind it.
  const afterMutation = () => {
    if (selectedId) loadDetail(selectedId);
    load({ silent: true });
  };

  const refresh = () => { setRefreshing(true); load({ silent: true }); };

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-3 py-6 sm:px-4 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-800">Registered users</h2>
          <p className="text-sm text-slate-500">Every QR Create account, with its allowance, plan and activity.</p>
        </div>
        <Button variant="secondary" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Section title="Accounts" icon={Users} className="p-0">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, email or phone…"
              className={`${inputClass} pl-9`}
            />
          </div>
          <select value={entitled} onChange={(e) => { setOffset(0); setEntitled(e.target.value); }} className={`${inputClass} w-44`}>
            <option value="">Any entitlement</option>
            <option value="pro">Pro right now</option>
            <option value="free">Free right now</option>
          </select>
          <select value={plan} onChange={(e) => { setOffset(0); setPlan(e.target.value); }} className={`${inputClass} w-40`}>
            <option value="">Any stored plan</option>
            {PLANS.map((p) => <option key={p} value={p}>{p === 'pro' ? 'Pro' : 'Free'}</option>)}
          </select>
          <select value={sort} onChange={(e) => { setOffset(0); setSort(e.target.value); }} className={`${inputClass} w-44`}>
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>

        {loading && data.users.length === 0 ? (
          <Loading label="Loading users…" />
        ) : data.users.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No users match these filters"
            hint="Clear the search or widen the plan filter."
            action={<Button variant="secondary" onClick={() => { setSearchInput(''); setPlan(''); setEntitled(''); }}>Clear filters</Button>}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3 text-right">Credits</th>
                    <th className="px-4 py-3 text-right">Free codes</th>
                    <th className="px-4 py-3 text-right">Codes</th>
                    <th className="px-4 py-3 text-right">Scans</th>
                    <th className="px-4 py-3">Registered</th>
                    <th className="px-4 py-3 text-right">
                      <ArrowUpDown className="ml-auto h-3.5 w-3.5 text-slate-300" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.users.map((u) => (
                    <tr key={u.user_id} className="cursor-pointer hover:bg-sky-50/40" onClick={() => setSelectedId(u.user_id)}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{fullName(u)}</div>
                        <div className="text-xs text-slate-400">{u.email}</div>
                      </td>
                      <td className="px-4 py-3"><PlanBadge subscription={u.subscription} /></td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-700">{fmtInt(u.credits?.credits_balance)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-500">{fmtInt(u.credits?.basic_remaining)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">{fmtInt(u.codes_total)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">{fmtInt(u.scans_total)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(u.registered_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-semibold text-sky-600">Open</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination total={data.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} noun="user" />
          </>
        )}
      </Section>

      <UserDetailModal
        open={!!selectedId}
        loading={detailLoading}
        detail={detail}
        onClose={() => setSelectedId(null)}
        onMutated={afterMutation}
      />
    </div>
  );
}

// ─── Account drawer ──────────────────────────────────────────────────────────

function UserDetailModal({ open, loading, detail, onClose, onMutated }) {
  const [tab, setTab] = useState('account');

  useEffect(() => { if (open) setTab('account'); }, [open]);

  const title = detail ? fullName(detail) : 'Account';

  return (
    <Modal open={open} title={title} subtitle={detail?.email} onClose={onClose} wide>
      {loading && !detail ? (
        <Loading label="Loading account…" />
      ) : !detail ? (
        <EmptyState title="Account not available" />
      ) : (
        <div className="space-y-5">
          <ProfileHeader user={detail} />

          <div className="flex flex-wrap gap-1 border-b border-slate-100 pb-2">
            {[
              { id: 'account', label: 'Account', icon: ShieldCheck },
              { id: 'codes', label: 'QR codes', icon: QrCode },
              { id: 'scans', label: 'Scan analytics', icon: ScanLine },
              { id: 'ledger', label: 'Credit ledger', icon: Coins },
              { id: 'plan', label: 'Plan history', icon: History },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  tab === t.id ? 'bg-sky-50 text-sky-700 ring-1 ring-sky-100' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === 'account' && (
            <div className="space-y-5">
              <CreditPanel user={detail} onMutated={onMutated} />
              <SubscriptionPanel user={detail} onMutated={onMutated} />
            </div>
          )}
          {tab === 'codes' && <UserCodes userId={detail.user_id} />}
          {tab === 'scans' && <ScanRollup stats={detail.scan_stats} />}
          {tab === 'ledger' && <CreditLedger entries={detail.credit_ledger} />}
          {tab === 'plan' && <PlanHistory events={detail.plan_history} />}
        </div>
      )}
    </Modal>
  );
}

function ProfileHeader({ user }) {
  return (
    <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 sm:grid-cols-4">
      <Detail icon={Mail} label="Email" value={user.email} sub={user.verified_email ? 'verified' : 'unverified'} />
      <Detail icon={Phone} label="Phone" value={user.phone || '—'} sub={user.phone ? (user.verified_phone ? 'verified' : 'unverified') : undefined} />
      <Detail icon={ShieldCheck} label="Signed up with" value={AUTH_LABELS[user.auth_source] || user.auth_source} />
      <Detail icon={Calendar} label="Registered" value={fmtDate(user.registered_at)} sub={`last seen ${fmtDate(user.last_login_at)}`} />
    </div>
  );
}

function Detail({ icon: Icon, label, value, sub }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <Icon className="h-3.5 w-3.5 text-slate-400" /> {label}
      </div>
      <p className="mt-0.5 truncate text-sm font-medium text-slate-800" title={value}>{value}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

// ─── Credits ─────────────────────────────────────────────────────────────────

function CreditPanel({ user, onMutated }) {
  const { adjustCredits } = useQRCreate();
  const credits = user.credits || {};

  const [amount, setAmount] = useState('');
  const [bucket, setBucket] = useState('credit');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const magnitude = Number(amount);
  const valid = Number.isFinite(magnitude) && magnitude > 0;

  const submit = async (sign) => {
    if (!valid) {
      toast.error('Enter an amount greater than zero');
      return;
    }
    setBusy(true);
    try {
      const result = await adjustCredits(user.user_id, {
        amount: sign * Math.round(magnitude),
        bucket,
        note,
      });

      const applied = Math.abs(result?.applied ?? 0);
      const noun = bucket === 'credit' ? 'credit' : 'free code';
      const verb = sign > 0 ? 'Added' : 'Removed';

      // A clamped removal moved less than was asked for. Say so plainly —
      // silently succeeding with a different number is how a support call ends
      // with the wrong balance.
      if (result?.clamped) {
        toast(`${verb} ${applied} ${noun}${applied === 1 ? '' : 's'} — the account only had that much left.`, { icon: '⚠️' });
      } else {
        toast.success(`${verb} ${applied} ${noun}${applied === 1 ? '' : 's'}`);
      }

      setAmount('');
      setNote('');
      onMutated?.();
    } catch (err) {
      toast.error(err.message || 'Failed to adjust credits');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 p-4">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
        <Coins className="h-4 w-4 text-sky-600" /> Credits
      </h4>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Credit balance" value={credits.credits_balance} accent />
        <Stat label="Free codes left" value={credits.basic_remaining} />
        <Stat label="Granted (lifetime)" value={credits.credits_granted} />
        <Stat label="Spent" value={credits.credits_spent} />
      </div>

      <div className="mt-4">
        <Meter
          label="Free plain-code quota used"
          value={credits.basic_quota_used || 0}
          total={credits.basic_quota || 0}
          hint="Credits buy customized codes; this quota covers plain ones. The two never borrow from each other."
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 sm:grid-cols-4">
        <Field label="Amount">
          <input
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            className={inputClass}
          />
        </Field>
        <Field label="Bucket">
          <select value={bucket} onChange={(e) => setBucket(e.target.value)} className={inputClass}>
            <option value="credit">Credits (customized codes)</option>
            <option value="basic">Free quota (plain codes)</option>
          </select>
        </Field>
        <Field label="Reason" className="sm:col-span-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Recorded in the ledger alongside your admin id"
            className={inputClass}
          />
        </Field>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button onClick={() => submit(1)} disabled={busy || !valid}>
          <Plus className="h-4 w-4" /> Add
        </Button>
        <Button variant="danger" onClick={() => submit(-1)} disabled={busy || !valid}>
          <Minus className="h-4 w-4" /> Remove
        </Button>
        <p className="text-xs text-slate-400">
          Removing more than the account holds takes it to zero rather than failing. Already-used free quota can't be taken back.
        </p>
      </div>
    </section>
  );
}

// ─── Subscription ────────────────────────────────────────────────────────────

function SubscriptionPanel({ user, onMutated }) {
  const { updateSubscription } = useQRCreate();

  // Memoized on the subscription itself: a fresh `{}` fallback on every render
  // would make `initial` a new object each time, and the effect below would then
  // reset the form under the operator mid-edit.
  const sub = useMemo(() => user.subscription || {}, [user.subscription]);

  const initial = useMemo(() => ({
    plan: sub.plan || 'free',
    status: sub.status || 'active',
    provider: sub.provider || 'manual',
    product_id: sub.product_id || '',
    provider_ref: sub.provider_ref || '',
    current_period_start: toDateInput(sub.current_period_start),
    current_period_end: toDateInput(sub.current_period_end),
    cancel_at_period_end: !!sub.cancel_at_period_end,
    note: '',
  }), [sub]);

  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const dirty = useMemo(
    () => Object.keys(initial).some((k) => k !== 'note' && form[k] !== initial[k]) || form.note.trim() !== '',
    [form, initial],
  );

  const submit = async () => {
    setBusy(true);
    try {
      await updateSubscription(user.user_id, {
        plan: form.plan,
        status: form.status,
        provider: form.provider,
        product_id: form.product_id,
        provider_ref: form.provider_ref,
        current_period_start: form.current_period_start,
        current_period_end: form.current_period_end,
        cancel_at_period_end: form.cancel_at_period_end,
        note: form.note,
      });
      toast.success(`Subscription set to ${form.plan === 'pro' ? 'Pro' : 'Free'}`);
      onMutated?.();
    } catch (err) {
      toast.error(err.message || 'Failed to update the subscription');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
          <CreditCard className="h-4 w-4 text-sky-600" /> Subscription
        </h4>
        <div className="flex items-center gap-2">
          <PlanBadge subscription={sub} />
          <StatusBadge status={sub.status} />
          {sub.synthesized && <Badge className="bg-slate-100 text-slate-500 ring-slate-200">Never subscribed</Badge>}
        </div>
      </div>

      {sub.current_period_end && (
        <p className="mb-3 text-xs text-slate-500">
          Current period ends {fmtDateTime(sub.current_period_end)}
          {sub.cancel_at_period_end && ' · will not renew'}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Plan" required>
          <select value={form.plan} onChange={set('plan')} className={inputClass}>
            {PLANS.map((p) => <option key={p} value={p}>{p === 'pro' ? 'Pro' : 'Free'}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select value={form.status} onChange={set('status')} className={inputClass}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Provider">
          <select value={form.provider} onChange={set('provider')} className={inputClass}>
            {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>

        <Field label="Period start" hint="Leave empty for none">
          <input type="date" value={form.current_period_start} onChange={set('current_period_start')} className={inputClass} />
        </Field>
        <Field label="Period end" hint="Empty means no expiry">
          <input type="date" value={form.current_period_end} onChange={set('current_period_end')} className={inputClass} />
        </Field>
        <Field label="Store product id">
          <input value={form.product_id} onChange={set('product_id')} placeholder="e.g. pro_monthly" className={inputClass} disabled={form.plan === 'free'} />
        </Field>

        <Field label="Provider reference" hint="The store's subscription id" className="sm:col-span-2">
          <input value={form.provider_ref} onChange={set('provider_ref')} className={inputClass} disabled={form.plan === 'free'} />
        </Field>
        <Field label="Reason" hint="Recorded in the plan history">
          <input value={form.note} onChange={set('note')} placeholder="e.g. comped for support case" className={inputClass} />
        </Field>
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={form.cancel_at_period_end} onChange={set('cancel_at_period_end')} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-200" />
        Do not renew at the end of the period
      </label>

      {form.plan === 'free' && (sub.provider_ref || sub.product_id) && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
          Dropping to Free clears the store linkage on this account, so the receipt can be attached to whoever really owns it.
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button onClick={submit} disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save subscription'}
        </Button>
        <Button variant="secondary" onClick={() => setForm(initial)} disabled={busy || !dirty}>
          <X className="h-4 w-4" /> Reset
        </Button>
      </div>
    </section>
  );
}

// ─── Drawer tabs ─────────────────────────────────────────────────────────────

function UserCodes({ userId }) {
  const { fetchUserCodes } = useQRCreate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchUserCodes(userId, { limit: 100, sort: 'created', order: 'desc' })
      .then((result) => { if (!cancelled) setCodes(result?.codes || []); })
      .catch(() => { if (!cancelled) setCodes([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, fetchUserCodes]);

  if (loading) return <Loading label="Loading codes…" />;
  if (codes.length === 0) return <EmptyState icon={QrCode} title="This account has not created any codes yet" />;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2.5">Code</th>
            <th className="px-4 py-2.5">Type</th>
            <th className="px-4 py-2.5 text-right">Scans</th>
            <th className="px-4 py-2.5">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {codes.map((c) => (
            <tr key={c.generation_id}>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-700">{c.name}</span>
                  {c.is_customized && <Badge className="bg-sky-50 text-sky-700 ring-sky-200">Customized</Badge>}
                  {!c.is_active && <Badge className="bg-slate-100 text-slate-500 ring-slate-200">Inactive</Badge>}
                </div>
                <div className="max-w-md truncate text-xs text-slate-400" title={c.target_url}>{c.target_url}</div>
              </td>
              <td className="px-4 py-2.5 text-slate-600">{c.qr_type}</td>
              <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-700">{fmtInt(c.scan_count)}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{fmtDate(c.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreditLedger({ entries }) {
  if (!entries || entries.length === 0) return <EmptyState icon={Coins} title="No credit movements recorded" />;

  const KIND_LABELS = {
    signup_grant: 'Welcome grant',
    purchase: 'Purchase',
    promo: 'Promo',
    admin_grant: 'Admin added',
    admin_revoke: 'Admin removed',
    spend: 'Spent',
    refund: 'Refund',
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2.5">When</th>
            <th className="px-4 py-2.5">Movement</th>
            <th className="px-4 py-2.5">Bucket</th>
            <th className="px-4 py-2.5 text-right">Amount</th>
            <th className="px-4 py-2.5 text-right">Balance after</th>
            <th className="px-4 py-2.5">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {entries.map((t) => (
            <tr key={t.transaction_id}>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{fmtDateTime(t.created_at)}</td>
              <td className="px-4 py-2.5 text-slate-700">{KIND_LABELS[t.kind] || t.kind}</td>
              <td className="px-4 py-2.5 text-slate-500">{t.bucket === 'credit' ? 'Credits' : 'Free codes'}</td>
              <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${t.amount < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {t.amount > 0 ? '+' : ''}{fmtInt(t.amount)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{fmtInt(t.balance_after)}</td>
              <td className="max-w-xs truncate px-4 py-2.5 text-slate-400" title={t.note || ''}>{t.note || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanHistory({ events }) {
  if (!events || events.length === 0) return <EmptyState icon={History} title="No plan changes recorded" />;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2.5">When</th>
            <th className="px-4 py-2.5">Change</th>
            <th className="px-4 py-2.5">Plan</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">By</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {events.map((e) => (
            <tr key={e.event_id}>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{fmtDateTime(e.created_at)}</td>
              <td className="px-4 py-2.5 font-medium text-slate-700">{e.kind?.replace(/_/g, ' ')}</td>
              <td className="px-4 py-2.5 text-slate-600">
                {e.plan_before || '—'} <span className="text-slate-300">→</span> <span className="font-semibold">{e.plan_after || '—'}</span>
              </td>
              <td className="px-4 py-2.5 text-slate-600">
                {e.status_before || '—'} <span className="text-slate-300">→</span> {e.status_after || '—'}
              </td>
              <td className="px-4 py-2.5 text-slate-400">
                {e.payload?.admin ? (
                  <span title={e.payload?.note || ''}>{e.payload.admin}{e.payload?.note ? ' · ' + e.payload.note : ''}</span>
                ) : (
                  e.provider || '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
